import React from "react";
import { ScanFace, ScanLine, Database, GitCompareArrows, ShieldCheck, Lightbulb } from "lucide-react";
import PageHero from "../components/PageHero";
import SectionHead from "../components/SectionHead";
import Reveal from "../components/Reveal";
import FigureBlock from "../components/FigureBlock";

const STEPS = [
    {
        icon: ScanLine,
        no: "Step 1",
        title: "Detection",
        body: "Software scans an image or video frame and locates every face, drawing an invisible bounding box around each one — regardless of who the person is or whether they consented.",
    },
    {
        icon: ScanFace,
        no: "Step 2",
        title: "Landmarking",
        body: "The system measures the face's geometry: the distance between the eyes, the shape of the jaw, the curve of the cheekbones. These measurements reduce a unique human face to a set of numbers.",
    },
    {
        icon: Database,
        no: "Step 3",
        title: "Template creation",
        body: "The measurements become a 'face template' — a mathematical signature, or embedding, that can be stored and compared. Unlike a photo, a template is designed to be searched at scale.",
    },
    {
        icon: GitCompareArrows,
        no: "Step 4",
        title: "Match / no match",
        body: "The template is compared against a database. Every comparison returns a confidence score — a probability, not a certainty. Somewhere along that line, a threshold turns 'possibly' into 'suspect'.",
    },
];

const GLOSSARY = [
    { term: "Template / embedding", def: "The numeric signature generated from a face, used instead of the image itself for searching." },
    { term: "1:1 verification", def: "Confirming one person's identity against one stored reference — e.g. unlocking a phone. The claim being tested is 'is this person who they say they are?'" },
    { term: "1:N identification", def: "Searching one unknown face against a database of many — the mode police use to find suspects. The claim being tested is 'whose face is this?'" },
    { term: "Confidence threshold", def: "The score above which the system declares a match. Set too low, innocent people are flagged; set too high, real offenders are missed." },
    { term: "False positive", def: "The system matches the wrong person — the central risk our project examines." },
];

export default function WhatIsFrt() {
    return (
        <div>
            <PageHero
                fileNo="FR-NZ-01"
                lines={["What is", <>facial <i className="text-amber">recognition</i>?</>]}
                lede="Facial Recognition Technology (FRT) is software that identifies or verifies a person from their facial features alone. It converts something deeply personal — a face — into something a database can search (Introna & Nissenbaum, 2010). That single conversion is where both its power and its danger live."
                meta={["SUBJECT: DEFINITIONS & MECHANICS"]}
            />

            <section className="border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
                    <SectionHead
                        no="1.1"
                        kicker="Mechanics"
                        title="Four steps from face to suspect"
                        lede="The pipeline below runs in fractions of a second and, in live mode, continuously — over every frame from every connected camera."
                    />
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
                        {STEPS.map((s, i) => (
                            <Reveal key={s.no} delay={i * 0.08} className="bg-ink">
                                <div className="p-7 sm:p-8 h-full flex flex-col" data-testid={`frt-step-card-${i + 1}`}>
                                    <s.icon size={22} className="text-amber mb-6" />
                                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ash mb-2">{s.no}</p>
                                    <h3 className="font-display text-xl text-paper mb-3">{s.title}</h3>
                                    <p className="text-sm leading-relaxed text-ash">{s.body}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-sheet border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 lg:gap-16">
                    <div>
                        <SectionHead
                            no="1.2"
                            kicker="Key terms"
                            title="A short glossary"
                            lede="Five terms carry most of the debate. Everything else in this dossier builds on them."
                        />
                        <dl className="border-t border-white/10">
                            {GLOSSARY.map((g, i) => (
                                <Reveal key={g.term} delay={i * 0.05}>
                                    <div className="border-b border-white/10 py-5" data-testid={`glossary-item-${i + 1}`}>
                                        <dt className="font-mono text-sm text-amber mb-1.5">{g.term}</dt>
                                        <dd className="text-sm leading-relaxed text-ash">{g.def}</dd>
                                    </div>
                                </Reveal>
                            ))}
                        </dl>
                    </div>
                    <div className="lg:pt-4">
                        <FigureBlock
                            num="2"
                            src="https://images.unsplash.com/photo-1520697830682-bbb6e85e2b0b?q=80&w=1600&auto=format&fit=crop"
                            alt="Close-up of a high-resolution optical camera lens"
                            caption="High-resolution optics are the first link in the biometric chain: recognition quality is capped by the quality of the image the sensor captures."
                            ratio="4/3"
                        />
                        <Reveal delay={0.1} className="mt-10">
                            <div className="border border-amber/30 bg-amber/[0.05] p-6 sm:p-8">
                                <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-amber mb-4">
                                    <Lightbulb size={14} /> Why accuracy is not equal
                                </p>
                                <p className="text-sm sm:text-base leading-relaxed text-paper/85">
                                    FRT performance depends heavily on the datasets used to train it. The US National
                                    Institute of Standards and Technology found that many 2018-era algorithms produced
                                    false positives for some demographic groups at rates up to 100 times higher than
                                    for others (Grother, Ngan &amp; Hanaoka, 2019). An earlier landmark study measured
                                    commercial gender-classification errors of 0.8% for lighter-skinned men but 34.7%
                                    for darker-skinned women (Buolamwini &amp; Gebru, 2018). Accuracy, in other words,
                                    is not one number — it is many, and they are not shared equally.
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.15} className="mt-6">
                            <div className="flex items-start gap-3 border border-white/10 p-6">
                                <ShieldCheck size={18} className="text-match shrink-0 mt-1" />
                                <p className="text-sm leading-relaxed text-ash">
                                    Consented 1:1 verification (like unlocking your phone) and non-consensual 1:N
                                    identification in public space are ethically different propositions — a distinction
                                    this dossier returns to on the Ethical &amp; Social Issues page.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-20">
                    <Reveal>
                        <p className="font-display text-2xl sm:text-3xl text-paper leading-snug max-w-4xl italic">
                            “The conversion of a face into data is not a neutral act: it makes a person searchable
                            without their knowledge, and searchable at population scale.”
                        </p>
                        <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.25em] text-ash">
                            — After Introna &amp; Nissenbaum (2010)
                        </p>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
